/**
 * @file billHaulageLink.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-07
 * @modified 2026-10-07
 * @description Third-party haulage links on the bill screens: a haulier's bill
 *              linked to a PO made in Xero that it carried the goods for (N
 *              Hayes' INV-0532 for Willett's PO-26135). Orders made here do
 *              this with a PO-H; a Xero-made PO has none. The link bills
 *              nothing — the PO's cost and status are not changed and nothing
 *              goes to Xero. The rules are the server's
 *              (utils/billHaulageLink.js); these are the words and the shapes
 *              the screens share.
 */

/** bill_purchase_orders.link_source for a third-party haulage link. */
export const HAULAGE_LINK_SOURCE = 'third_party_haulage';

/** Is this linked-PO row a haulage link? */
export const isHaulageLink = (po) => !!po && po.link_source === HAULAGE_LINK_SOURCE;

const numberOf = (po) => po?.purchase_order_number || (po?.id ? `PO #${po.id}` : 'the PO');

/** "Haulage for PO-26135" — the label wherever a bill shows such a PO. */
export const haulageForLabel = (po) => `Haulage for ${numberOf(po)}`;

/** The question asked before linking — the server's wording, for a PO the screen already has. */
export function haulageOfferMessage(po) {
  const who = po?.contact_name || po?.supplier_name;
  return `This bill is the haulage for ${numberOf(po)}${who ? ` (${who})` : ''}. The PO's cost and status are not changed.`;
}

/**
 * A save refused with HAULAGE_LINK_OFFERED: the POs to move from the goods
 * list to the haulage list, and the question to ask. Null for any other answer.
 *
 * @param {object} data — the 409 body ({ code, offers: [{ id, message }] })
 * @returns {{ ids: number[], message: string }|null}
 */
export function haulageOfferFrom(data) {
  if (!data || data.code !== 'HAULAGE_LINK_OFFERED') return null;
  const offers = Array.isArray(data.offers) ? data.offers : [];
  if (!offers.length) return null;
  return {
    ids: offers.map(o => Number(o.id)).filter(Number.isInteger),
    message: offers.map(o => o.message).filter(Boolean).join(' ') || data.error || '',
  };
}

/**
 * Move offered POs from the goods list to the haulage list, and take them off
 * any bill line that named them (a line names the PO its goods were bought
 * on; a haulier's line names none).
 *
 * @param {{ goodsIds: number[], haulageIds: number[], lines?: object[] }} state
 * @param {number[]} ids
 */
export function moveToHaulage({ goodsIds = [], haulageIds = [], lines = [] }, ids) {
  const move = new Set((ids || []).map(Number));
  return {
    goodsIds: goodsIds.filter(id => !move.has(Number(id))),
    haulageIds: [...new Set([...haulageIds.map(Number), ...move])],
    lines: lines.map(l => (l && move.has(Number(l.purchase_order_id))
      ? { ...l, purchase_order_id: null, po_line_key: null }
      : l)),
  };
}

/** Labels for a bill's POs in a list or an export: goods by number, haulage marked. */
export function billPoLabels(bill) {
  const goods = (Array.isArray(bill?.linked_pos) ? bill.linked_pos : []).map(p => p.purchase_order_number).filter(Boolean);
  const haulage = (Array.isArray(bill?.haulage_pos) ? bill.haulage_pos : []).map(p => `${p.purchase_order_number} (haulage)`);
  return [...goods, ...haulage];
}
